import React, { useState, useEffect } from 'react';
import { AdminLayout } from './AdminLayout';
import { commentsAPI } from '../../lib/api';
import { Loader2, MessageSquare, Check, X, AlertTriangle, Trash2, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { formatDateShort } from '../../lib/utils';
import { Link } from 'react-router-dom';

export const AdminComments = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [comments, setComments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedIds, setSelectedIds] = useState([]);
  const limit = 20;

  useEffect(() => {
    fetchData();
  }, [page, statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, commentsRes] = await Promise.all([
        commentsAPI.getStats(),
        commentsAPI.getAll({ skip: page * limit, limit, status: statusFilter !== 'all' ? statusFilter : undefined })
      ]);
      setStats(statsRes.data);
      setComments(commentsRes.data.comments);
      setTotal(commentsRes.data.total);
      setSelectedIds([]);
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast.error('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await commentsAPI.update(id, { status: newStatus });
      toast.success(`Comment ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update comment');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    
    try {
      await commentsAPI.delete(id);
      toast.success('Comment deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) {
      toast.error('No comments selected');
      return;
    }
    
    if (action === 'delete' && !window.confirm(`Delete ${selectedIds.length} comments?`)) return;
    
    try {
      await commentsAPI.bulkAction(selectedIds, action);
      toast.success(`${selectedIds.length} comments ${action}d`);
      fetchData();
    } catch (error) {
      toast.error('Failed to perform bulk action');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === comments.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(comments.map(c => c.id));
    }
  };

  if (loading && !comments.length) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-comments">
        {/* Header */}
        <div>
          <h1 className="font-serif text-2xl md:text-3xl font-bold">Comments</h1>
          <p className="text-muted-foreground">Moderate and manage comments</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="cursor-pointer hover:bg-secondary/20" onClick={() => setStatusFilter('all')}>
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">Total</p>
              <p className="text-2xl font-bold">{stats?.total || 0}</p>
            </CardContent>
          </Card>
          <Card className={`cursor-pointer hover:bg-secondary/20 ${statusFilter === 'pending' ? 'ring-2 ring-primary' : ''}`} onClick={() => setStatusFilter('pending')}>
            <CardContent className="pt-4">
              <p className="text-sm text-yellow-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{stats?.pending || 0}</p>
            </CardContent>
          </Card>
          <Card className={`cursor-pointer hover:bg-secondary/20 ${statusFilter === 'approved' ? 'ring-2 ring-primary' : ''}`} onClick={() => setStatusFilter('approved')}>
            <CardContent className="pt-4">
              <p className="text-sm text-green-600">Approved</p>
              <p className="text-2xl font-bold text-green-600">{stats?.approved || 0}</p>
            </CardContent>
          </Card>
          <Card className={`cursor-pointer hover:bg-secondary/20 ${statusFilter === 'rejected' ? 'ring-2 ring-primary' : ''}`} onClick={() => setStatusFilter('rejected')}>
            <CardContent className="pt-4">
              <p className="text-sm text-red-600">Rejected</p>
              <p className="text-2xl font-bold text-red-600">{stats?.rejected || 0}</p>
            </CardContent>
          </Card>
          <Card className={`cursor-pointer hover:bg-secondary/20 ${statusFilter === 'spam' ? 'ring-2 ring-primary' : ''}`} onClick={() => setStatusFilter('spam')}>
            <CardContent className="pt-4">
              <p className="text-sm text-orange-600">Spam</p>
              <p className="text-2xl font-bold text-orange-600">{stats?.spam || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Bulk Actions */}
        {selectedIds.length > 0 && (
          <div className="flex items-center gap-2 p-3 bg-secondary/50 rounded-sm">
            <span className="text-sm font-medium">{selectedIds.length} selected</span>
            <Button size="sm" variant="outline" onClick={() => handleBulkAction('approve')}>
              <Check className="h-4 w-4 mr-1" /> Approve
            </Button>
            <Button size="sm" variant="outline" onClick={() => handleBulkAction('reject')}>
              <X className="h-4 w-4 mr-1" /> Reject
            </Button>
            <Button size="sm" variant="outline" onClick={() => handleBulkAction('spam')}>
              <AlertTriangle className="h-4 w-4 mr-1" /> Spam
            </Button>
            <Button size="sm" variant="destructive" onClick={() => handleBulkAction('delete')}>
              <Trash2 className="h-4 w-4 mr-1" /> Delete
            </Button>
          </div>
        )}

        {/* Comments List */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>
              {statusFilter === 'all' ? 'All Comments' : `${statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)} Comments`} ({total})
            </CardTitle>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="spam">Spam</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            {comments.length > 0 ? (
              <>
                <div className="space-y-4">
                  <div className="flex items-center gap-2 pb-2 border-b border-border">
                    <input
                      type="checkbox"
                      checked={selectedIds.length === comments.length && comments.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded"
                    />
                    <span className="text-sm text-muted-foreground">Select all</span>
                  </div>
                  
                  {comments.map((comment) => (
                    <div key={comment.id} className="border border-border rounded-sm p-4 space-y-3">
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(comment.id)}
                          onChange={() => toggleSelect(comment.id)}
                          className="mt-1 rounded"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-medium">{comment.author_name}</span>
                              <span className="text-muted-foreground text-sm ml-2">({comment.author_email})</span>
                            </div>
                            <span className={`px-2 py-1 text-xs rounded-sm ${
                              comment.status === 'approved' ? 'bg-green-100 text-green-700' :
                              comment.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                              comment.status === 'spam' ? 'bg-orange-100 text-orange-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {comment.status}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            on <Link to={`/article/${comment.article_slug}`} className="text-primary hover:underline" target="_blank">
                              {comment.article_title}
                              <ExternalLink className="h-3 w-3 inline ml-1" />
                            </Link>
                            <span className="mx-2">·</span>
                            {formatDateShort(comment.created_at)}
                          </p>
                          <p className="mt-2 text-sm bg-secondary/30 p-3 rounded-sm">{comment.content}</p>
                          
                          <div className="flex gap-2 mt-3">
                            {comment.status !== 'approved' && (
                              <Button size="sm" variant="outline" onClick={() => handleStatusChange(comment.id, 'approved')}>
                                <Check className="h-4 w-4 mr-1" /> Approve
                              </Button>
                            )}
                            {comment.status !== 'rejected' && (
                              <Button size="sm" variant="outline" onClick={() => handleStatusChange(comment.id, 'rejected')}>
                                <X className="h-4 w-4 mr-1" /> Reject
                              </Button>
                            )}
                            {comment.status !== 'spam' && (
                              <Button size="sm" variant="outline" onClick={() => handleStatusChange(comment.id, 'spam')}>
                                <AlertTriangle className="h-4 w-4 mr-1" /> Spam
                              </Button>
                            )}
                            <Button size="sm" variant="destructive" onClick={() => handleDelete(comment.id)}>
                              <Trash2 className="h-4 w-4 mr-1" /> Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {total > limit && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                    <p className="text-sm text-muted-foreground">
                      Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
                    </p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 0}>
                        Previous
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={(page + 1) * limit >= total}>
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No {statusFilter !== 'all' ? statusFilter : ''} comments</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminComments;
